import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Stack} from '@mui/material';
import {Modal} from '@lightricks/react-design-system';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useContactSales from '@contentlab/hooks/use-contact-sales';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {CREDIT_ACCESS_ACTION} from '@contentlab/utils/resolveCreditAccessAction';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import {removeDraftProjectId} from '@/utils/LtidStorage';
import styles from './CreditEndModal.module.scss';
import CreditEndModalContent from './CreditEndModalContent';

const viewLocationMap = {
  [ROUTES.CONTENT_LAB_DUB]: CONTENT_LAB_VIEW_NAMES.DUB,
  [ROUTES.CONTENT_LAB_REPHRASE]: CONTENT_LAB_VIEW_NAMES.REPHRASE,
  [ROUTES.CONTENT_LAB_RESIZE]: CONTENT_LAB_VIEW_NAMES.RESIZE,
  [ROUTES.CONTENT_LAB_CAPTIONS]: CONTENT_LAB_VIEW_NAMES.CAPTIONS,
  [ROUTES.CONTENT_LAB_AI_AVATAR]: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
};

const getViewLocation = (route: string) => {
  return (
    viewLocationMap[route as keyof typeof viewLocationMap] ||
    CONTENT_LAB_VIEW_NAMES.HOME
  );
};

function CreditEndModal() {
  const location = useLocation();
  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {uiInteraction} = useUiInteractionEvent();
  const {creditEndModalState} = contentLabStore(
    useShallow((state) => ({
      creditEndModalState: state.creditEndModalState,
    }))
  );
  const [presentationId, setPresentationId] = useState<string>('');
  const handleClose = () => {
    contentLabActions.setCreditEndModalState(null);
    removeDraftProjectId();
    const interactionId = uuid();
    uiInteraction({
      item_name: 'close_credit_end_modal',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
      interaction_id: interactionId,
    });
    viewDismissed({
      presentation_id: presentationId,
      interaction_id: interactionId,
      reason: 'modal_closed',
    });
  };
  const onContactSalesClick = useContactSales({
    flowName: 'credit-end-contact-sales',
    interactionLocation: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
  });
  const handleRedirectToPricing = () => {
    contentLabActions.setCreditEndModalState(null);
    const interactionId = uuid();
    uiInteraction({
      item_name: 'contact_sales',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
      interaction_id: interactionId,
    });
    viewDismissed({
      presentation_id: presentationId,
      interaction_id: interactionId,
      reason: 'contact_sales_button_pressed',
    });
    onContactSalesClick();
  };

  useEffect(() => {
    if (creditEndModalState) {
      const newPresentationId = uuid();
      setPresentationId(newPresentationId);
      viewPresented({
        presentation_id: newPresentationId,
        view_name: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
        view_type: 'popup',
        view_location: getViewLocation(location.pathname),
        source: 'editor_generate',
      });
    }
  }, [creditEndModalState]);
  return (
    <Modal
      showCloseButton
      open={
        !!(
          creditEndModalState &&
          creditEndModalState !== CREDIT_ACCESS_ACTION.NO_OP
        )
      }
      testID="token-end-modal"
      width={800}
      handleClose={handleClose}
      fullScreen={false}
      contentClassName={styles.modalContent}
    >
      <Stack direction="row" width="800px" height="600px">
        <CreditEndModalContent
          status={creditEndModalState || ''}
          onClose={handleClose}
          onRedirectToPricing={handleRedirectToPricing}
        />
        <img
          src="https://storage.googleapis.com/brands-public-assets/content-lab/PricingModal.png"
          alt="Pricing Modal"
          className={styles.image}
        />
      </Stack>
    </Modal>
  );
}

export default CreditEndModal;
